/**
 * TeamSystem Impact - OAuth Client List
 * Mostra i sistemi AI connessi via OAuth e le sessioni attive.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Cloud as CloudIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { oauthClientApi, OAuthClientDTO, OAuthSessionDTO } from '../api/oauthClient.api';

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) + '...' : id;
}

export function OAuthClientList() {
  const queryClient = useQueryClient();
  const [revokeTarget, setRevokeTarget] = useState<OAuthClientDTO | null>(null);

  const { data: clients, isLoading, error } = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: oauthClientApi.list,
  });

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['oauth-sessions'],
    queryFn: oauthClientApi.listSessions,
  });

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) => oauthClientApi.revoke(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] });
      queryClient.invalidateQueries({ queryKey: ['oauth-sessions'] });
      setRevokeTarget(null);
    },
  });

  return (
    <Box>
      {/* --- Sistemi Connessi (OAuth Clients) --- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CloudIcon color="primary" />
        <Typography variant="h6">Sistemi Connessi</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento dei client OAuth
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : clients && clients.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Registrato il</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client: OAuthClientDTO) => (
                <TableRow key={client.client_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {client.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={truncateId(client.client_id)}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(client.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoca client">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setRevokeTarget(client)}
                        aria-label={`Revoca client ${client.client_name}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Nessun sistema AI connesso. I client OAuth vengono registrati automaticamente quando un sistema esterno si connette.
          </Typography>
        </Paper>
      )}

      {/* --- Sessioni Attive --- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
        <PeopleIcon color="primary" />
        <Typography variant="h6">Sessioni Attive</Typography>
      </Box>

      {sessionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sessions && sessions.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Utente</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Connettore</TableCell>
                <TableCell>Connesso dal</TableCell>
                <TableCell>Scade il</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session: OAuthSessionDTO, idx: number) => (
                <TableRow key={`${session.userId}-${session.client_id}-${idx}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {session.userName.trim() || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {session.userEmail || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.client_name}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(session.createdAt)}</TableCell>
                  <TableCell>{formatDate(session.expiresAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Nessuna sessione attiva. Le sessioni appaiono quando gli utenti si autenticano via OAuth.
          </Typography>
        </Paper>
      )}

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoca Client OAuth</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler revocare il client <strong>{revokeTarget?.client_name}</strong>?
            Il sistema non potra' piu' accedere alla piattaforma.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Annulla</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.client_id)}
            disabled={revokeMutation.isPending}
          >
            {revokeMutation.isPending ? 'Revoca in corso...' : 'Revoca'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
